<template>
  <v-container fluid>
    <div v-if="isLoadingStaff" class="pt-6">
      <div class="loading-state">
        <v-progress-circular indeterminate color="primary" />
        <p class="loading-text">Loading staff details...</p>
      </div>
    </div>

    <v-form v-else @submit.prevent="handleSubmit">
      <div class="details-stack">
        <div class="detail-field">
          <AppTextField
            v-model="firstName"
            label="First Name"
            label-icon="mdi-account-outline"
            placeholder="John"
            auto-complete="first_name"
            :error-messages="
              showValidationErrors && firstNameError ? [firstNameError] : []
            "
            :rules="[required]"
            @update:modelValue="syncValidation"
          />
        </div>
        <div class="detail-field">
          <AppTextField
            v-model="lastName"
            label="Last Name"
            auto-complete="last_name"
            label-icon="mdi-account-outline"
            placeholder="Doe"
            :error-messages="showValidationErrors && lastNameError ? [lastNameError] : []"
            :rules="[required]"
            @update:modelValue="syncValidation"
          />
        </div>
        <div class="detail-field">
          <AppMobileField
            v-model="phone"
            label="Phone Number"
            label-icon="mdi-phone-outline"
            placeholder="(888) 888 8888"
            :error-messages="showValidationErrors && phoneError ? [phoneError] : []"
            :rules="[required, phoneRule]"
            @update:modelValue="syncValidation"
          />
        </div>
        <div class="detail-field">
          <AppTextField
            v-model="email"
            :readonly="false"
            label="Email"
            label-icon="mdi-email-outline"
            placeholder="info@example.com"
            type="email"
            :error-messages="showValidationErrors && emailError ? [emailError] : []"
            :rules="[required, emailRule]"
            @update:modelValue="syncValidation"
          />
        </div>
        <div class="detail-field">
          <div class="px-2">
            <AppFieldLabel icon="mdi-shield-account-outline" label="Assign Role(s)" />
          </div>
          <div class="d-flex flex-wrap bg-white px-2 py-4" style="border-radius: 12px">
            <v-checkbox
              v-for="item in roleItems"
              :key="String(item.id)"
              :label="item.label"
              :disabled="rolesLoading"
              :model-value="role.includes(item.id)"
              inline
              density="compact"
              class="mr-4"
              hide-details
              @update:model-value="(checked) => onRoleToggle(item.id, checked)"
            />
          </div>
          <div
            v-if="showValidationErrors && roleError"
            class="mt-1 ml-4"
            style="color: #dc2626 !important; font-size: 13px"
          >
            {{ roleError }}
          </div>
        </div>
      </div>
      <div class="mt-8">
        <AppButtonFlat type="submit" block :loading="isSubmitting" prepend-icon="mdi-content-save">
          Submit
        </AppButtonFlat>
      </div>
    </v-form>
  </v-container>
</template>

<script setup lang="ts">

import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppFieldLabel from "@/shared/components/ui/AppFieldLabel.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { computed, onMounted, ref, watch } from "vue";
import { fetchRoles, normalizeRoles } from "@/shared/services/roles.api";
import {
  handleSubmitStaff,
  type StaffPayload,
  fetchStaffDetails,
} from "@/modules/vendor/services/vendors.api";
import { useRouter, useRoute } from "vue-router";
import AppMobileField from "@/shared/components/ui/AppMobileField.vue";

/* keep only what's used by the template (form + subtitle counter) */
const staffCount = ref(0);
const staffsSubtitle = computed(() => {
    const count = staffCount.value;
    return count === 1 ? "1 staff member" : `${count} staff members`;
});

const router = useRouter();
const route = useRoute();

/* form state */
const showForm = ref(false);
const isSubmitting = ref(false);
const firstName = ref("");
const lastName = ref("");
const email = ref("");
const phone = ref("");
const role = ref<Array<number | string>>([]);

// editing state: when `id` present in route query/params, load details
const staffId = ref<string | null>(null);
const isLoadingStaff = ref(false);
const loadStaffError = ref<string | null>(null);

const rolesLoading = ref(false);
type RoleItem = { id: number | string; label: string; role_name?: string | null };
const roleItems = ref<RoleItem[]>([]);

const firstNameError = ref("");
const lastNameError = ref("");
const emailError = ref("");
const phoneError = ref("");
const roleError = ref("");
const showValidationErrors = ref(false);

const required = (value: unknown) => Boolean(String(value ?? "").trim()) || "Required";
const emailRule = (value: unknown) => /.+@.+\..+/.test(String(value ?? "").trim()) || "Enter a valid email";
const phoneRule = (value: unknown) => Boolean(String(value ?? "").trim()) || "Enter a valid phone number";

function openForm() {
    showForm.value = true;
    resetForm();
}

function closeForm() {
    showForm.value = false;
    resetForm();
}

function resetForm() {
    firstName.value = "";
    lastName.value = "";
    email.value = "";
    phone.value = "";
    role.value = [];
    firstNameError.value = "";
    lastNameError.value = "";
    emailError.value = "";
    phoneError.value = "";
    roleError.value = "";
    showValidationErrors.value = false;
}

function validate() {
    firstNameError.value = "";
    lastNameError.value = "";
    emailError.value = "";
    phoneError.value = "";
    roleError.value = "";

    if (!firstName.value.trim()) firstNameError.value = "First name is required";
    if (!lastName.value.trim()) lastNameError.value = "Last name is required";
    if (!email.value.trim()) emailError.value = "Email is required";
    else if (!/.+@.+\..+/.test(email.value)) emailError.value = "Enter a valid email";
    if (!phone.value.trim()) phoneError.value = "Phone is required";
    if (!Array.isArray(role.value) || role.value.length === 0) {
        roleError.value = "Role is required";
    }

    return !firstNameError.value && !lastNameError.value && !emailError.value && !phoneError.value && !roleError.value;
}

function syncValidation() {
    if (!showValidationErrors.value) return;
    validate();
}

function onRoleToggle(value: RoleItem["id"], checked: boolean) {
    const idx = role.value.findIndex((v) => v === value);
    if (checked) {
        if (idx === -1) role.value.push(value);
        return;
    }
    if (idx !== -1) role.value.splice(idx, 1);
}

function normalizeRoleName(value: unknown) {
  return String(value ?? "").trim().toLowerCase().replace(/\s+/g, "_");
}

function getSelectedRoleIds(
  selectedRoles: Array<{ id?: number | string; label?: string; role_name?: string | null }>,
  availableRoles: RoleItem[],
) {
  const selectedNames = new Set(
    selectedRoles
      .map((roleItem) => normalizeRoleName(roleItem.role_name || roleItem.label))
      .filter(Boolean),
  );

  return availableRoles
    .filter((roleItem) => selectedNames.has(normalizeRoleName(roleItem.role_name || roleItem.label)))
    .map((roleItem) => roleItem.id);
}

watch([firstName, lastName, email, phone, role], syncValidation, { deep: true });

onMounted(async () => {
	    // if an id is present in query or params, fetch staff details and populate form for editing
	    const idFromQuery = String(route.query.id || route.params.id || "").trim();
	    if (!idFromQuery) {
	        rolesLoading.value = true;
	        try {
	            const payload = await fetchRoles();
	            roleItems.value = normalizeRoles(payload);
	        } catch (err) {
	            // eslint-disable-next-line no-console
	            console.error("Roles fetch failed", err);
	        } finally {
            rolesLoading.value = false;
        }
        return;
    }

    staffId.value = idFromQuery;
    isLoadingStaff.value = true;
	    rolesLoading.value = true;
	    loadStaffError.value = null;
	    try {
	        const staffPayload = await fetchStaffDetails(idFromQuery);
	        const s = (staffPayload && (staffPayload as any).data) || {};
	        const client = (s && (s as any).client) || {};

	        firstName.value = String(client.fname || "").trim();
	        lastName.value = String(client.lname || "").trim();
	        email.value = String(client.email || "").trim();
	        phone.value = String(client.phone || "").trim();

	        roleItems.value = normalizeRoles({
            roles: (staffPayload as any)?.roles_available || [],
          }).map((item) => {
            const available = (staffPayload as any)?.roles_available || [];
            const matched = Array.isArray(available)
              ? available.find((r: any) => String(r?.id) === String(item.id))
              : null;
            return {
              ...item,
              role_name: matched?.role_name ?? null,
            };
          });
	        role.value = getSelectedRoleIds(Array.isArray((s as any).roles) ? (s as any).roles : [], roleItems.value);
        showForm.value = true;
    } catch (err: any) {
        loadStaffError.value = (err && err.message) || "Failed to load staff details";
        // eslint-disable-next-line no-console
        console.error("fetchStaffDetails failed", err);
    } finally {
        isLoadingStaff.value = false;
        rolesLoading.value = false;
        showValidationErrors.value = false;
    }
});

async function handleSubmit() {
  showValidationErrors.value = true;
  if (!validate()) return;
  isSubmitting.value = true;

  try {
    const payload: StaffPayload = {
      ...(staffId.value ? { id: staffId.value } : {}),
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
      email: email.value.trim(),
      phone: phone.value.trim(),
      assigned_roles: role.value,
    };
    await handleSubmitStaff(payload);
    closeForm();
    router.push({ name: "vendor.staffs" }).catch(() => undefined);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Create staff failed", error);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.details-stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.detail-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 0;
}

.loading-text {
  margin: 0;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.55);
}
</style>
